import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessagesSquare, ListChecks, Activity, Wrench, Send, Save, Target, Flag, CheckCircle2, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { Card, PageTitle, StatusPill, btnPrimary, btnGhost, fieldCls } from "@/components/portal/ui";
import { trackEvent } from "@/lib/api";

const RADIO = (value, options, onChange, testid) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)}
        className={`px-4 py-2 rounded-full text-sm border transition-colors ${value === o ? "bg-[#9F634E] border-[#9F634E] text-white" : "border-[#E5E1D8] text-[#1A1918] hover:border-[#9F634E]/50"}`}>{o}</button>
    ))}
  </div>
);
const Field = ({ label, children }) => <div><label className="block text-sm text-[#6B665E] mb-2">{label}</label>{children}</div>;

export default function Guided() {
  const nav = useNavigate();
  const [d, setD] = useState(undefined);
  const [ob, setOb] = useState({});
  const [msg, setMsg] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [tab, setTab] = useState("today");
  const timer = useRef();
  const firedRef = useRef(false);

  const load = () => http.get("/portal/guided").then((r) => { setD(r.data); if (r.data?.onboarding) setOb(r.data.onboarding); if (r.data && !firedRef.current) { firedRef.current = true; trackEvent("guided_consulting_started", {}, { once: true }); } }).catch(() => setD(null));
  useEffect(() => { load(); }, []);

  if (d === undefined) return <div className="text-[#6B665E]">Loading…</div>;

  if (!d) return (
    <div>
      <PageTitle eyebrow="Guided Support" title="Your Guided Virtual Consulting" sub="Direct support from your Certified Infant Sleep Consultant while you implement your plan." />
      <Card className="p-8 text-center">
        <MessagesSquare className="mx-auto text-[#9F634E]" size={40} />
        <p className="font-display text-2xl text-[#1A1918] mt-4">Your guided journey isn't active</p>
        <p className="text-[#6B665E] mt-2 text-sm">If you already know you want a complete plan plus professional implementation support, start here.</p>
        <button onClick={() => nav("/guided-infant-sleep-consulting")} className={`${btnPrimary} mt-6`}>Explore the Guided Virtual Consulting — $749</button>
      </Card>
    </div>
  );

  // Gate: Guided starts with Clarity, then Blueprint, before onboarding.
  if (!d.clarity_completed) return <StageGate title="Your Guided journey begins with Clarity" body="Every family starts with the Clarity stage. Complete your Clarity intake first — your guided support builds on what your Certified Infant Sleep Consultant learns." cta="Start your Clarity intake" to="/portal/diagnostic" nav={nav} />;
  if (!d.plan_completed) return <StageGate title="Next: your Personalized Blueprint" body="Before guided implementation begins, we complete your full Blueprint so there's a clear plan to support you through." cta="Complete your Blueprint intake" to="/portal/blueprint-intake" nav={nav} />;

  const set = (k, v) => {
    setOb((prev) => {
      const next = { ...prev, [k]: v };
      clearTimeout(timer.current);
      timer.current = setTimeout(async () => { try { await http.put("/portal/intake/guided", { answers: next }); setSavedAt(new Date()); } catch (e) {} }, 700);
      return next;
    });
  };

  // Onboarding (only once, after Clarity + Blueprint, before active support)
  if (!d.onboarded) {
    const submit = async () => {
      try { await http.put("/portal/intake/guided", { answers: ob }); await http.post("/portal/intake/guided/submit"); toast.success("Guided support is now active"); load(); }
      catch (e) { toast.error("Could not submit"); }
    };
    return (
      <div>
        <PageTitle eyebrow="Guided onboarding · one last step" title="Let's set up your guided support" sub="We already have your Clarity and Blueprint — no need to repeat those. Just a few implementation details so your Certified Infant Sleep Consultant can support you well." />
        <div className="flex items-center gap-2 text-sm text-[#8A998C] mb-5"><Save size={14} /> {savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Your progress saves automatically"}</div>
        <div className="max-w-2xl space-y-4">
          <Card className="p-6 space-y-5">
            <Field label="When would you like to start implementing?"><input type="date" data-testid="guided-start-date" className={fieldCls} value={ob.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Field>
            <Field label="Preferred way to communicate">{RADIO(ob.comms, ["In-portal messaging", "Email", "Text"], (v) => set("comms", v), "guided-comms")}</Field>
            <Field label="How often would you like to check in?">{RADIO(ob.checkin_rhythm, ["Daily", "Every few days", "Weekly"], (v) => set("checkin_rhythm", v), "guided-rhythm")}</Field>
            <Field label="What does great support look like for you?"><textarea rows={2} data-testid="guided-expectations" className={fieldCls} value={ob.support_expectations || ""} onChange={(e) => set("support_expectations", e.target.value)} /></Field>
            <Field label="What challenges do you anticipate?"><textarea rows={2} className={fieldCls} value={ob.anticipated_challenges || ""} onChange={(e) => set("anticipated_challenges", e.target.value)} /></Field>
            <Field label="Who else in the household is involved / supportive?"><textarea rows={2} className={fieldCls} value={ob.household_support || ""} onChange={(e) => set("household_support", e.target.value)} /></Field>
            <Field label="Anything on your mind before you begin?"><textarea rows={2} className={fieldCls} value={ob.current_concerns || ""} onChange={(e) => set("current_concerns", e.target.value)} /></Field>
            <Field label="Anything change since your Blueprint was created?"><textarea rows={2} className={fieldCls} value={ob.recent_changes || ""} onChange={(e) => set("recent_changes", e.target.value)} /></Field>
          </Card>
          <div className="flex gap-3">
            <button onClick={async () => { await http.put("/portal/intake/guided", { answers: ob }); toast.success("Progress saved"); }} className={btnGhost}>Save & continue later</button>
            <button data-testid="guided-onboard-submit" onClick={submit} className={btnPrimary}>Start my guided support <ArrowRight size={15} className="inline ml-1" /></button>
          </div>
        </div>
      </div>
    );
  }

  // Active workspace — organized into labeled sub-sections.
  const sendMsg = async () => {
    if (!msg.trim()) return;
    try { await http.post("/portal/messages", { product: "guided", text: msg }); setMsg(""); load(); } catch (e) { toast.error("Could not send"); }
  };
  const guidance = d.guidance || [];
  const adjustments = guidance.filter((g) => g.kind === "adjustment");
  const recs = guidance.filter((g) => g.kind === "recommendation" || g.kind === "milestone" || g.kind === "focus");
  const unread = (d.messages || []).filter((m) => m.sender !== "customer" && m.read_by_customer === false).length;

  const TABS = [
    ["today", "Today / Tonight", Target],
    ["plan", "My Plan", ListChecks],
    ["adjustments", "Adjustments", Wrench],
    ["messages", "Messages", MessagesSquare],
    ["log", "Sleep Log", Activity],
    ["status", "Service Status", Flag],
  ];

  return (
    <div>
      <PageTitle eyebrow="Guided Support" title="Your Guided Virtual Consulting" sub="Direct support from your Certified Infant Sleep Consultant while you implement your plan." />

      <div className="flex flex-wrap gap-1.5 mb-6 border-b border-[#E5E1D8]" data-testid="guided-tabs">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`gtab-${key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm rounded-t-lg -mb-px border-b-2 transition-colors ${tab === key ? "border-[#9F634E] text-[#9F634E] font-medium" : "border-transparent text-[#6B665E] hover:text-[#1A1918]"}`}>
            <Icon size={14} />{label}{key === "messages" && unread > 0 && <span className="ml-1 text-[10px] bg-[#9F634E] text-white rounded-full px-1.5 py-0.5">{unread}</span>}
          </button>
        ))}
      </div>

      {tab === "today" && (
        <div className="space-y-5" data-testid="gpanel-today">
          {d.current_focus ? (
            <Card className="p-6 border-[#9F634E]/25 bg-[#9F634E]/5" data-testid="guided-current-focus">
              <div className="eyebrow text-[#9F634E] mb-2 flex items-center gap-1.5"><Target size={14} /> This week's focus</div>
              <p className="text-[#1A1918] text-[15px] leading-relaxed whitespace-pre-wrap">{d.current_focus}</p>
            </Card>
          ) : <Card className="p-6"><p className="text-[#6B665E] text-sm">Your Certified Infant Sleep Consultant will set this week's focus here as you progress.</p></Card>}
          <Card className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div><p className="font-display text-lg text-[#1A1918]">How did last night go?</p><p className="text-[#6B665E] text-sm mt-0.5">Log tonight so your Certified Infant Sleep Consultant can see what's working.</p></div>
            <button onClick={() => nav("/portal/progress")} className={`${btnPrimary} shrink-0`}>Log tonight's sleep</button>
          </Card>
        </div>
      )}

      {tab === "plan" && (
        <div className="space-y-5" data-testid="gpanel-plan">
          <Card className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div><p className="font-display text-lg text-[#1A1918]">Your Personalized Blueprint</p><p className="text-[#6B665E] text-sm mt-0.5">The complete plan your guided support follows.</p></div>
            <button onClick={() => nav("/portal/blueprint")} className={`${btnGhost} shrink-0`}>Open my plan</button>
          </Card>
          <Card className="p-6">
            <div className="eyebrow text-[#9F634E] mb-4 flex items-center gap-1.5"><Wrench size={14} /> Consultant guidance</div>
            {recs.length === 0 ? <p className="text-[#6B665E] text-sm">Guidance from your Certified Infant Sleep Consultant will appear here as you progress.</p> : (
              <div className="space-y-3" data-testid="guided-guidance">
                {recs.map((g, i) => (
                  <div key={i} className="border-b border-[#F0ECE4] pb-3 last:border-0">
                    <div className="flex items-center gap-1.5 text-[#8A998C] text-xs mb-1 capitalize">{g.kind === "milestone" ? <Flag size={12} className="text-[#667A66]" /> : <Target size={12} />} {g.kind}</div>
                    <p className="text-[#1A1918] text-[15px] leading-relaxed whitespace-pre-wrap">{g.text}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "adjustments" && (
        <Card className="p-6" data-testid="gpanel-adjustments">
          <div className="eyebrow text-[#9F634E] mb-4">Plan adjustments</div>
          {adjustments.length === 0 ? <p className="text-[#6B665E] text-sm">Any adjustments to your plan will be recorded here.</p> : (
            <div className="space-y-3" data-testid="guided-adjustments">
              {adjustments.map((g, i) => (
                <div key={i} className="border-l-2 border-[#9F634E] pl-3">
                  <p className="text-[#1A1918] text-[15px] leading-relaxed whitespace-pre-wrap">{g.text}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "messages" && (
        <Card className="p-6" data-testid="gpanel-messages">
          <div className="eyebrow text-[#9F634E] mb-4 flex items-center gap-1.5"><MessagesSquare size={14} /> Questions for your Certified Infant Sleep Consultant</div>
          <div className="space-y-3 mb-4 max-h-96 overflow-y-auto" data-testid="guided-messages">
            {(d.messages || []).length === 0 ? <p className="text-[#6B665E] text-sm">No messages yet. Ask your Certified Infant Sleep Consultant anything about your plan.</p> :
              d.messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[15px] ${m.sender === "customer" ? "bg-[#9F634E] text-white" : "bg-[#F3EFE9] text-[#1A1918]"}`}>
                    <p className="whitespace-pre-wrap">{m.text}</p>
                  </div>
                </div>
              ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="guided-message-input" className={fieldCls} placeholder="Type your question…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendMsg(); }} />
            <button data-testid="guided-message-send" onClick={sendMsg} className={`${btnPrimary} shrink-0 flex items-center gap-1.5`}><Send size={15} /> Send</button>
          </div>
        </Card>
      )}

      {tab === "log" && (
        <Card className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="gpanel-log">
          <div><p className="font-display text-lg text-[#1A1918]">Sleep Log</p><p className="text-[#6B665E] text-sm mt-0.5">Record each night and watch your trends build over time.</p></div>
          <button onClick={() => nav("/portal/progress")} className={`${btnPrimary} shrink-0`}>Open Sleep Log</button>
        </Card>
      )}

      {tab === "status" && (
        <Card className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="gpanel-status">
          <div>
            <div className="eyebrow text-[#8A998C] mb-1">Service status</div>
            <p className="text-[#1A1918] text-[15px]">{d.service?.state === "Completed" ? "Your guided support is complete." : "Your Certified Infant Sleep Consultant is supporting your implementation."}</p>
          </div>
          <StatusPill state={d.service?.state} />
        </Card>
      )}
    </div>
  );
}

function StageGate({ title, body, cta, to, nav }) {
  return (
    <div>
      <PageTitle eyebrow="Guided Support" title="Your Guided Virtual Consulting" sub="Your guided support follows the same professional sequence every family gets." />
      <Card className="p-8 text-center">
        <CheckCircle2 className="mx-auto text-[#9F634E]" size={40} />
        <p className="font-display text-2xl text-[#1A1918] mt-4">{title}</p>
        <p className="text-[#6B665E] mt-2 text-sm max-w-md mx-auto">{body}</p>
        <button onClick={() => nav(to)} className={`${btnPrimary} mt-6`}>{cta}</button>
      </Card>
    </div>
  );
}
